import {
  FormField,
  Radio,
  SelectionList,
  TextInput,
} from "@caprikorn/dynamo-ui";
import type { FC } from "react";
import { Controller } from "react-hook-form";

import { emailRegex, phoneNumberRegex } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./ContactDetailForm.module.css";
import type { ContactDetailsFormProps } from "./ContactDetailsForm.types";

const civilities: string[] = ["M.", "Mme"];

export const ContactDetailsForm: FC<ContactDetailsFormProps> = ({
  name,
  control,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);

  return (
    <div className={classes.container} data-testid={`${name}-RMFormWrapper`}>
      <div className={classes.civility}>
        <Controller
          control={control}
          name={`${name}.civility`}
          render={({ field, fieldState }) => (
            <FormField
              as="div"
              hasError={Boolean(fieldState.error)}
              hint={
                fieldState.error?.message &&
                t(fieldState.error.message, { ns: "common" })
              }
              label={t("requestDrawer.contactDetails.civility")}
            >
              <SelectionList className={classes.select}>
                {civilities.map((civility) => (
                  <Radio
                    key={civility}
                    {...field}
                    checked={field.value === civility}
                    hasError={Boolean(fieldState.error)}
                    label={civility}
                    value={civility}
                  />
                ))}
              </SelectionList>
            </FormField>
          )}
          rules={{ required: "form.error.required" }}
        />
      </div>

      <Controller
        control={control}
        name={`${name}.lastName`}
        render={({ field, fieldState }) => (
          <TextInput
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("requestDrawer.contactDetails.lastname")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value}
          />
        )}
        rules={{ required: "form.error.required" }}
      />

      <Controller
        control={control}
        name={`${name}.firstName`}
        render={({ field, fieldState }) => (
          <TextInput
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("requestDrawer.contactDetails.firstname")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value}
          />
        )}
        rules={{ required: "form.error.required" }}
      />

      <Controller
        control={control}
        name={`${name}.phone`}
        render={({ field, fieldState }) => (
          <TextInput
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("requestDrawer.contactDetails.phoneNumber")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value}
          />
        )}
        rules={{
          required: "form.error.required",
          pattern: {
            value: phoneNumberRegex,
            message: "form.error.invalidFormat",
          },
        }}
      />

      <Controller
        control={control}
        name={`${name}.email`}
        render={({ field, fieldState }) => (
          <TextInput
            hasError={Boolean(fieldState.error)}
            hint={
              fieldState.error?.message &&
              t(fieldState.error.message, { ns: "common" })
            }
            label={t("requestDrawer.contactDetails.email")}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value}
          />
        )}
        rules={{
          required: "form.error.required",
          pattern: {
            value: emailRegex,
            message: "form.error.invalidFormat",
          },
        }}
      />
    </div>
  );
};
